'use client';

import {
	createContext,
	useContext,
	useEffect,
	useState,
	ReactNode,
} from 'react';
import { analyticsService } from '../api/analytics-service';
import type { IAnalyticsContext } from '../types/analytics.types';
import { useFirebase } from '@/core/firebase';
import { logger } from '@/core/logger';

const AnalyticsContext = createContext<IAnalyticsContext | undefined>(
	undefined
);

export interface AnalyticsProviderProps {
	children: ReactNode;
}

export function AnalyticsProvider({ children }: AnalyticsProviderProps) {
	const { app } = useFirebase();
	const [isInitialized, setIsInitialized] = useState(false);
	const [error, setError] = useState<string | null>(null);

	useEffect(() => {
		if (!app) {
			// Firebase is still initializing
			return;
		}

		let mounted = true;

		const initializeAnalytics = async () => {
			try {
				// Initialize analytics (Firebase + Clarity)
				const success = await analyticsService.initialize(app);
				if (!success) {
					logger.info(
						'Analytics initialization skipped (services not configured or not in browser)'
					);
				}

				if (mounted) {
					setIsInitialized(true);
					setError(null);
				}
			} catch (err) {
				logger.error('Analytics initialization error', err);
				if (mounted) {
					setError(
						err instanceof Error
							? err.message
							: 'Analytics initialization failed'
					);
					setIsInitialized(true); // Still mark as initialized to prevent infinite loading
				}
			}
		};

		initializeAnalytics();

		return () => {
			mounted = false;
		};
	}, [app]);

	const value: IAnalyticsContext = {
		isInitialized,
		error,
		logEvent: (
			eventName: string,
			eventParameters?: Record<string, any> // eslint-disable-line @typescript-eslint/no-explicit-any
		) => analyticsService.logEvent(eventName, eventParameters),
		logError: (
			eventName: string,
			eventParameters?: Record<string, any> // eslint-disable-line @typescript-eslint/no-explicit-any
		) => analyticsService.logEvent(eventName, eventParameters),
		identify: (
			userId: string,
			userProperties?: Record<string, any> // eslint-disable-line @typescript-eslint/no-explicit-any
		) => analyticsService.identify(userId, userProperties),
	};

	return (
		<AnalyticsContext.Provider value={value}>
			{children}
		</AnalyticsContext.Provider>
	);
}

export function useAnalytics(): IAnalyticsContext {
	const context = useContext(AnalyticsContext);

	if (!context) {
		throw new Error(
			'useAnalytics must be used within an AnalyticsProvider'
		);
	}

	return context;
}
